import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Customer, CustomerRelationshipLog } from '../../types/customer';
import { ClockIcon, UserIcon, PlusIcon, PencilIcon, ExclamationCircleIcon, CheckCircleIcon, FunnelIcon, PhoneIcon, EnvelopeIcon, BuildingOfficeIcon, UserGroupIcon, ArchiveBoxIcon, TrashIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import CustomerRelationshipLogForm from './CustomerRelationshipLogForm';
import { doc, updateDoc, arrayUnion, arrayRemove, Timestamp, getDocs, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import EditRelationshipLogModal from './EditRelationshipLogModal';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { getUserDisplayNameSync } from '../../utils/userUtils';

interface CustomerRelationshipLogTabProps {
  logs: CustomerRelationshipLog[] | undefined;
  customerId: string;
  customerName: string;
  customer: Customer;
  onLogsUpdated: () => Promise<void>;
}

const CustomerRelationshipLogTab: React.FC<CustomerRelationshipLogTabProps> = ({ 
  logs, 
  customerId,
  customerName,
  customer,
  onLogsUpdated
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logToEdit, setLogToEdit] = useState<CustomerRelationshipLog | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filterOpenOnly, setFilterOpenOnly] = useState(false);

  // Delete confirmation modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<CustomerRelationshipLog | null>(null);

  // Reset logToEdit when it's null
  useEffect(() => {
    if (!showEditModal) {
      setLogToEdit(null);
    }
  }, [showEditModal]);

  // Function to generate the entry ID based on requirements
  const generateEntryId = async () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    
    try {
      // Get all customers to count today's total logs across the system
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      let todayLogsCount = 0;
      
      customersSnapshot.forEach((doc) => {
        const customerData = doc.data();
        if (customerData.relationshipLogs && customerData.relationshipLogs.length > 0) {
          customerData.relationshipLogs.forEach((log: any) => {
            const logDate = log.createdAt instanceof Date 
              ? log.createdAt 
              : log.createdAt.toDate();
            if (logDate.getFullYear() === date.getFullYear() &&
                logDate.getMonth() === date.getMonth() &&
                logDate.getDate() === date.getDate()) {
              todayLogsCount++;
            }
          });
        }
      });
      
      // Calculate sequence number (todayLogsCount + 1, formatted as 4 digits)
      const seqNum = String(todayLogsCount + 1).padStart(4, '0');
      
      // Determine prefix (first 8 chars of tax number or customer ID)
      let prefix = '';
      if (customer.type === 'organization' && customer.taxNumber) {
        prefix = customer.taxNumber.substring(0, 8);
      } else {
        prefix = customer.id.substring(0, 8);
      }
      
      return `${prefix}_${dateStr}_${seqNum}`;
    } catch (error) {
      console.error('Error generating entry ID:', error);
      // Fallback to timestamp-based ID if Firebase query fails
      return `${customer.id.substring(0, 8)}_${dateStr}_${Date.now().toString().slice(-4)}`;
    }
  };

  const handleAddLog = async (content: string, contactType: 'phone' | 'email' | 'distributor' | 'internal' | 'postal') => {
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      const newLogEntry: CustomerRelationshipLog = {
        id: uuidv4(),
        customerId,
        entryId: await generateEntryId(),
        content,
        contactType,
        createdAt: Timestamp.now(),
        createdBy: user.uid,
        createdByName: user.displayName || undefined,
        isOpen: true // New logs are open by default
      };
      
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        relationshipLogs: arrayUnion(newLogEntry)
      });
      
      setShowAddForm(false);
      // Wait for the onLogsUpdated callback to complete
      await onLogsUpdated();
      toast.success(t('customers.relationshipLog.addSuccess') || 'Log entry added successfully');
    } catch (error) {
      console.error('Failed to add log entry:', error);
      toast.error(t('customers.relationshipLog.addError') || 'Failed to add log entry');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setShowAddForm(false);
  };

  // Function to handle editing a log entry
  const handleEditClick = (log: CustomerRelationshipLog) => {
    setLogToEdit(log);
    setShowEditModal(true);
  };

  // Handle successful edit from EditRelationshipLogModal
  const handleEditSuccess = async () => {
    await onLogsUpdated();
    setShowEditModal(false);
  };

  // Function to handle deleting a log entry
  const handleDeleteLog = async (log: CustomerRelationshipLog) => {
    if (!user) {
      toast.error('Bejelentkezés szükséges a törléshez');
      return;
    }

    // Super admin törölhet mindent, más csak a saját bejegyzését
    const canDelete = user.role === 'super_admin' || log.createdBy === user.uid;
    
    if (!canDelete) {
      toast.error('Csak a bejegyzés létrehozója vagy a rendszergazda törölheti');
      return;
    }

    setIsDeleteModalOpen(true);
    setLogToDelete(log);
  };

  // Filtered logs based on filter settings
  const filteredLogs = useMemo(() => {
    return logs 
      ? [...logs].filter(log => !filterOpenOnly || log.isOpen)
      : [];
  }, [logs, filterOpenOnly]);

  // Sort logs by creation date (newest first)
  const sortedLogs = useMemo(() => {
    return filteredLogs.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt.toDate();
      const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt.toDate();
      return dateB.getTime() - dateA.getTime();
    });
  }, [filteredLogs]);

  // Kapcsolattípus ikonok és színek
  const getContactTypeDisplay = (contactType: string) => {
    switch (contactType) {
      case 'phone':
        return { 
          icon: PhoneIcon, 
          label: 'Telefon', 
          color: 'text-blue-600 bg-blue-50',
          borderColor: 'border-blue-500'
        };
      case 'email':
        return { 
          icon: EnvelopeIcon, 
          label: 'Email', 
          color: 'text-green-600 bg-green-50',
          borderColor: 'border-green-500'
        };
      case 'distributor':
        return { 
          icon: BuildingOfficeIcon, 
          label: 'Elosztói', 
          color: 'text-orange-600 bg-orange-50',
          borderColor: 'border-orange-500'
        };
      case 'internal':
        return { 
          icon: UserGroupIcon, 
          label: 'Belső', 
          color: 'text-purple-600 bg-purple-50',
          borderColor: 'border-purple-500'
        };
      case 'postal':
        return { 
          icon: ArchiveBoxIcon, 
          label: 'Postai', 
          color: 'text-gray-600 bg-gray-50',
          borderColor: 'border-gray-500'
        };
      default:
        return { 
          icon: PhoneIcon, 
          label: 'Telefon', 
          color: 'text-blue-600 bg-blue-50',
          borderColor: 'border-blue-500'
        };
    }
  };

  return (
    <div className="space-y-4 p-4">
      {/* Header with controls */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <h3 className="text-lg font-medium text-gray-900">
          {t('customers.relationshipLog.title') || 'Customer Relationship Log'}
        </h3>
        
        <div className="flex items-center gap-3">
          {/* Filter toggle */}
          <button
            type="button"
            onClick={() => setFilterOpenOnly(!filterOpenOnly)}
            className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              filterOpenOnly 
                ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 focus:ring-amber-500' 
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500'
            }`}
          >
            <FunnelIcon className="h-4 w-4 mr-1" />
            {t('customers.relationshipLog.filterOpenOnly') || 'Open tasks only'}
            {filterOpenOnly && (
              <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs">
                {sortedLogs.length}
              </span>
            )}
          </button>
          
          {/* Add button */}
          {!showAddForm && (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              {t('customers.relationshipLog.add') || 'Add new log entry'}
            </button>
          )}
        </div>
      </div>

      {showAddForm && (
        <CustomerRelationshipLogForm 
          customer={customer}
          onAddLog={handleAddLog}
          onCancel={handleCancelEdit}
        />
      )}

      {sortedLogs.length === 0 && !showAddForm ? (
        <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <DocumentArrowDownIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filterOpenOnly 
                ? 'Nincs nyitott ügy' 
                : 'Még nincsenek bejegyzések'}
            </h3>
            <p className="text-gray-500">
              {filterOpenOnly 
                ? 'Jelenleg nincsenek nyitott feladatok ehhez az ügyfélhez.' 
                : 'Kezdje el az első kapcsolattartási napló bejegyzés létrehozását.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedLogs.map((log) => {
            const createdAt = log.createdAt instanceof Date ? log.createdAt : log.createdAt.toDate();
            const formattedDate = new Intl.DateTimeFormat('hu-HU', {
              year: '2-digit',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }).format(createdAt);

            const isCurrentUserCreator = user && log.createdBy === user.uid;
            const canDeleteLog = user && (user.role === 'super_admin' || log.createdBy === user.uid);
            const contactTypeDisplay = getContactTypeDisplay(log.contactType || 'phone');
            const ContactTypeIcon = contactTypeDisplay.icon;

            return (
              <div 
                key={log.id} 
                className={`group relative bg-white rounded-xl border-l-4 ${contactTypeDisplay.borderColor} border border-gray-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 hover:border-gray-300`}
              >
                {/* Subtle gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                
                <div className="relative p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {/* Entry ID with enhanced styling */}
                        <span className="inline-flex items-center text-xs font-mono text-gray-600 bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                          #{log.entryId.split('_').pop() || log.entryId.substring(-6)}
                        </span>
                        
                        {/* Contact Type Badge with enhanced styling */}
                        <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${contactTypeDisplay.color} ring-1 ring-black/5`}>
                          <ContactTypeIcon className="h-3.5 w-3.5 mr-1.5" />
                          {contactTypeDisplay.label}
                        </div>
                        
                        {/* Status Badge with enhanced styling */}
                        <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ring-1 ring-black/5 ${
                          log.isOpen 
                            ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800' 
                            : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800'
                        }`}>
                          {log.isOpen ? '⚡ Aktív' : '✨ Befejezve'}
                        </div>
                      </div>
                      
                      {/* Enhanced Content */}
                      <div className="text-sm text-gray-800 mb-4 whitespace-pre-wrap bg-gradient-to-br from-gray-50 to-gray-100/50 p-4 rounded-lg border border-gray-200/50 leading-relaxed">
                        {log.content}
                      </div>
                      
                      {/* Enhanced Footer with better visual hierarchy */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center text-xs text-gray-500">
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></div>
                            <ClockIcon className="h-3.5 w-3.5 mr-1.5" />
                            <span className="font-medium">{formattedDate}</span>
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></div>
                            <UserIcon className="h-3.5 w-3.5 mr-1.5" />
                            <span className="font-medium">{log.createdByName || getUserDisplayNameSync(log.createdBy, user)}</span>
                          </div>
                        </div>
                        
                        {/* Priority indicator for open items */}
                        {log.isOpen && (
                          <div className="inline-flex items-center text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-1.5 animate-pulse"></div>
                            Figyelmet igényel
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Enhanced Actions */}
                    {(isCurrentUserCreator || canDeleteLog) && (
                      <div className="flex items-center gap-1 ml-4 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                        {/* Szerkesztés gomb csak a létrehozó számára */}
                        {isCurrentUserCreator && (
                          <button
                            onClick={() => handleEditClick(log)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-md"
                            title={t('customers.relationshipLog.edit') || 'Edit log entry'}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Törlés gomb a létrehozó vagy super admin számára */}
                        {canDeleteLog && (
                          <button
                            onClick={() => handleDeleteLog(log)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-md"
                            title={t('customers.relationshipLog.delete') || 'Delete log entry'}
                            disabled={isSubmitting}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Relationship Log Modal */}
      <EditRelationshipLogModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        logToEdit={logToEdit}
        customer={customer}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && logToDelete && (
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setLogToDelete(null);
          }}
          onConfirm={async () => {
            setIsSubmitting(true);
            try {
              // Remove the log entry from Firebase
              const customerRef = doc(db, 'customers', customerId);
              await updateDoc(customerRef, {
                relationshipLogs: arrayRemove(logToDelete)
              });

              toast.success(t('customers.relationshipLog.deleteSuccess') || 'Bejegyzés sikeresen törölve');
              await onLogsUpdated();
            } catch (error) {
              console.error('Failed to delete log entry:', error);
              toast.error(t('customers.relationshipLog.deleteError') || 'Nem sikerült törölni a bejegyzést');
            } finally {
              setIsDeleteModalOpen(false);
              setLogToDelete(null);
              setIsSubmitting(false);
            }
          }}
          title="Bejegyzés törlése"
          deleteMessage="Biztosan törölni szeretné ezt a kapcsolattartási bejegyzést?"
          deleteWarning="Ez a művelet nem vonható vissza. A bejegyzés véglegesen törlődik."
        />
      )}
    </div>
  );
};

export default CustomerRelationshipLogTab; 